import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  Typography
} from "@mui/material";
import { styled } from "@mui/material/styles";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { DateCalendar } from "@mui/x-date-pickers/DateCalendar";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { PickersDay, PickersDayProps } from "@mui/x-date-pickers/PickersDay";
import { addDays, format, isSameDay, isWithinInterval, startOfDay, subDays } from "date-fns";
import { useState } from "react";

interface CustomPickerDayProps extends PickersDayProps {
  isSelected: boolean;
  isHovered: boolean;
  isWeekStart?: boolean;
  isWeekEnd?: boolean;
}

const CUSTOM_PROPS: (keyof Omit<CustomPickerDayProps, keyof PickersDayProps>)[] = [
  "isSelected",
  "isHovered",
  "isWeekStart",
  "isWeekEnd"
];

const CustomPickersDay = styled(PickersDay, {
  shouldForwardProp: (prop) =>
    !(CUSTOM_PROPS as readonly string[]).includes(prop as string)
})<CustomPickerDayProps>(({ theme, isSelected, isHovered, isWeekStart, isWeekEnd }) => ({
  borderRadius: 0,
  ...(isSelected && {
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.primary.contrastText,
    "&:hover, &:focus": {
      backgroundColor: theme.palette.primary.main
    }
  }),
  ...(isHovered && {
    backgroundColor: theme.palette.primary.light,
    "&:hover, &:focus": {
      backgroundColor: theme.palette.primary.light
    }
  }),
  ...(isWeekStart && {
    borderTopLeftRadius: "50%",
    borderBottomLeftRadius: "50%"
  }),
  ...(isWeekEnd && {
    borderTopRightRadius: "50%",
    borderBottomRightRadius: "50%"
  })
})) as React.ComponentType<CustomPickerDayProps>;

const isInWeekRange = (day: Date, startDay: Date | null | undefined) => {
  if (startDay == null) {
    return false;
  }
  const normalizedStart = startOfDay(startDay);
  const endDay = addDays(normalizedStart, 6);
  return isWithinInterval(startOfDay(day), { start: normalizedStart, end: endDay });
};

const isWeekStart = (day: Date, startDay: Date | null | undefined) => {
  if (startDay == null) {
    return false;
  }
  return isSameDay(day, startDay);
};

const isWeekEnd = (day: Date, startDay: Date | null | undefined) => {
  if (startDay == null) {
    return false;
  }
  const endDay = addDays(startDay, 6);
  return isSameDay(day, endDay);
};

interface DayComponentProps extends PickersDayProps {
  selectedDay?: Date | null;
  hoveredDay?: Date | null;
}

const DayComponent = (props: DayComponentProps) => {
  const { day, selectedDay, hoveredDay, ...other } = props;

  return (
    <CustomPickersDay
      {...other}
      day={day}
      sx={{ px: 2.5 }}
      disableMargin
      selected={false}
      isSelected={isInWeekRange(day, selectedDay)}
      isHovered={isInWeekRange(day, hoveredDay)}
      isWeekStart={isWeekStart(day, selectedDay) || isWeekStart(day, hoveredDay)}
      isWeekEnd={isWeekEnd(day, selectedDay) || isWeekEnd(day, hoveredDay)}
    />
  );
};

interface WeekPickerProps {
  value: Date | null;
  onChange: (startDate: Date, endDate: Date) => void;
  label?: string;
  subtitle?: string;
}

const WeekPicker = ({ value, onChange, label = "Week", subtitle = "Select a week" }: WeekPickerProps) => {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [hoveredDay, setHoveredDay] = useState<Date | null>(null);
  const [tempValue, setTempValue] = useState<Date | null>(value);

  const defaultStart = subDays(new Date(), 7);

  const displayValue = value || defaultStart;
  const weekStart = displayValue;
  const weekEnd = addDays(displayValue, 6);

  const formatWeekDisplay = () => {
    const startStr = format(weekStart, "MMM d");
    const endStr = format(weekEnd, "MMM d, yyyy");
    return `${startStr} - ${endStr}`;
  };

  const handleOpenDialog = () => {
    setTempValue(displayValue);
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setHoveredDay(null);
  };

  const handleApply = () => {
    if (tempValue) {
      const selectedWeekStart = tempValue;
      const selectedWeekEnd = addDays(selectedWeekStart, 6);
      onChange(selectedWeekStart, selectedWeekEnd);
    }
    handleCloseDialog();
  };

  const handleDateChange = (newValue: Date | null) => {
    setTempValue(newValue);
  };

  return (
    <Box>
      <Typography variant="subtitle1" gutterBottom>
        {label}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {subtitle}
      </Typography>
      <Button
        variant="outlined"
        onClick={handleOpenDialog}
        fullWidth
        sx={{
          justifyContent: "flex-start",
          textTransform: "none",
          py: 1,
          px: 2
        }}
      >
        {formatWeekDisplay()}
      </Button>

      <Dialog
        open={dialogOpen}
        onClose={handleCloseDialog}
        maxWidth="xs"
        fullWidth
      >
        <DialogContent sx={{ p: 2 }}>
          <Typography variant="h6" gutterBottom>
            Select Week
          </Typography>
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <DateCalendar
              value={tempValue}
              onChange={handleDateChange}
              showDaysOutsideCurrentMonth
              maxDate={new Date()}
              slots={{ day: DayComponent }}
              slotProps={{
                day: {
                  selectedDay: tempValue,
                  hoveredDay,
                  onPointerEnter: (e: React.PointerEvent<HTMLButtonElement>) => {
                    const dayValue = e.currentTarget.getAttribute("data-timestamp");
                    if (dayValue) {
                      setHoveredDay(new Date(parseInt(dayValue)));
                    }
                  },
                  onPointerLeave: () => setHoveredDay(null)
                } as Partial<DayComponentProps>
              }}
            />
          </LocalizationProvider>
        </DialogContent>
        <DialogActions sx={{ px: 2, pb: 2 }}>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button onClick={handleApply} variant="contained" disabled={!tempValue}>
            Apply
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default WeekPicker;
